import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { notify } from '../utils/notify';
import { useTranslation } from 'react-i18next';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GSI_SRC = 'https://accounts.google.com/gsi/client';

/** True only when the app can actually sign someone in with Google. */
export const isGoogleSignInAvailable = () => Boolean(GOOGLE_CLIENT_ID);

function loadGoogleScript() {
    if (window.google?.accounts?.id) {
        return Promise.resolve();
    }

    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', resolve);
            existing.addEventListener('error', reject);
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = GSI_SRC;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

/**
 * Real Google sign-in. Renders Google's own button, which hands us an ID token
 * that the backend verifies at POST /api/auth/google.
 *
 * When VITE_GOOGLE_CLIENT_ID is not set, nothing is rendered at all — a button
 * that cannot sign anyone in has no business being on screen.
 */
export default function GoogleSignInButton({ withDivider = true, dividerText }) {
    const containerRef = useRef(null);
    const [failed, setFailed] = useState(false);
    const { googleLogin } = useAuthStore();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return;

        let cancelled = false;

        loadGoogleScript()
            .then(() => {
                if (cancelled || !containerRef.current) return;

                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: async ({ credential }) => {
                        const result = await googleLogin(credential);
                        if (result.success) {
                            navigate('/dashboard');
                        } else {
                            notify.error(result.error);
                        }
                    }
                });

                window.google.accounts.id.renderButton(containerRef.current, {
                    theme: 'outline',
                    size: 'large',
                    width: containerRef.current.offsetWidth || 320,
                    text: 'continue_with',
                    locale: i18n.language
                });
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });

        return () => {
            cancelled = true;
        };
    }, [googleLogin, navigate, i18n.language]);

    if (!GOOGLE_CLIENT_ID) return null;

    return (
        <>
            {failed ? (
                <p className="text-sm text-slate-500 text-center">{t('login.google_unavailable')}</p>
            ) : (
                <div ref={containerRef} className="flex justify-center [&>div]:w-full"></div>
            )}

            {withDivider && (
                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-700"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-slate-900 text-slate-500">{dividerText || t('login.or_continue_email')}</span>
                    </div>
                </div>
            )}
        </>
    );
}
